import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Building2, Users, DoorOpen, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

/**
 * Themed modal for admins to open a Deal Room between any startup and any
 * investor. Two entry modes:
 *
 *   • mode="startup"  — pinned startup, admin picks the counterparty investor
 *   • mode="investor" — pinned investor, admin picks the counterparty startup
 *
 * Flow: pick counterparty → optional summary → CONFIRM step → POST /admin/deal-rooms/manual.
 * Idempotent on the server: reused rooms flag `reused: true` in the response.
 */
export default function AddToDealRoomModal({
  open, onClose, mode, pinned, onCreated,
}) {
  const isStartupMode = mode === "startup";
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null);
  const [summary, setSummary] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setPicked(null); setSummary(""); setConfirming(false); setSearch("");
    const url = isStartupMode ? "/admin/investors" : "/admin/startups";
    api.get(url)
      .then((r) => setCandidates(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, isStartupMode]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => {
      const hay = isStartupMode
        ? [c.investor_name, c.firm_name, c.email, c.owner_email, c.city].filter(Boolean).join(" ")
        : [c.startup_name, c.founder_email, c.sector, c.legal_entity_name].filter(Boolean).join(" ");
      return hay.toLowerCase().includes(q);
    });
  }, [candidates, search, isStartupMode]);

  const pinnedLabel = isStartupMode
    ? (pinned?.startup_name || pinned?.founder_email || pinned?.startup_id || "—")
    : (pinned?.investor_name || pinned?.firm_name || pinned?.owner_email || pinned?.investor_id || "—");

  const pickedLabel = isStartupMode
    ? (picked?.investor_name || picked?.firm_name || picked?.email || picked?.owner_email || "—")
    : (picked?.startup_name || picked?.founder_email || "—");

  const submit = async () => {
    if (!picked) { toast.error("Select a counterparty first."); return; }
    const startup_id = isStartupMode ? pinned?.startup_id : picked?.startup_id;
    const investor_owner_user_id = isStartupMode
      ? (picked?.owner_user_id || picked?.investor_id)
      : (pinned?.owner_user_id || pinned?.investor_id);
    if (!startup_id || !investor_owner_user_id) {
      toast.error("Missing IDs — refresh and try again.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/deal-rooms/manual", {
        startup_id,
        investor_owner_user_id,
        deal_summary: summary || undefined,
      });
      const drId = r.data?.deal_room?.deal_room_id;
      if (r.data?.reused) toast.success(`Reused existing deal room ${drId}.`);
      else toast.success(`Deal room ${drId} opened.`);
      if (onCreated) onCreated(r.data?.deal_room);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-ink/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose} data-testid="add-to-deal-room-modal">
      <div className="relative w-full max-w-2xl max-h-[92vh] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy/10 bg-navy/[0.03]">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted flex items-center gap-1.5">
              <DoorOpen size={11} /> Open Deal Room (admin)
            </div>
            <div className="font-heading text-ink mt-0.5">
              {isStartupMode ? (
                <><Building2 size={14} className="inline mr-1 text-gold" />{pinnedLabel}</>
              ) : (
                <><Users size={14} className="inline mr-1 text-gold" />{pinnedLabel}</>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink"
                    data-testid="add-to-dr-close" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {!confirming ? (
          <>
            <div className="px-5 py-3 border-b border-navy/10">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={isStartupMode ? "Search investors by name, firm, email…" : "Search startups by name, sector, founder…"}
                className="w-full text-sm bg-white border border-navy/15 rounded px-3 py-2 text-ink focus:border-gold outline-none"
                data-testid="add-to-dr-search"
              />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-navy/[0.06]" data-testid="add-to-dr-list">
              {loading && (
                <div className="p-6 text-center text-ink-muted text-sm flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading candidates…
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="p-6 text-center text-ink-muted text-sm italic">No candidates match.</div>
              )}
              {!loading && filtered.map((c) => {
                const id = isStartupMode ? (c.investor_id || c.owner_user_id) : c.startup_id;
                const title = isStartupMode ? (c.investor_name || c.firm_name || c.email || id) : (c.startup_name || c.founder_email || id);
                const sub = isStartupMode
                  ? [c.firm_name, c.city, c.investor_type].filter(Boolean).join(" · ")
                  : [c.sector, c.business_stage, c.city].filter(Boolean).join(" · ");
                const kycOk = c.kyc_status === "Approved";
                const isPicked = picked && id === (isStartupMode ? (picked.investor_id || picked.owner_user_id) : picked.startup_id);
                return (
                  <button
                    key={id}
                    onClick={() => setPicked(c)}
                    className={`w-full text-left px-5 py-3 transition-colors ${isPicked ? "bg-gold/[0.10]" : "hover:bg-navy/[0.03]"}`}
                    data-testid={`add-to-dr-candidate-${id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-heading text-ink truncate">{title}</div>
                        {sub && <div className="text-[11px] font-mono text-ink-muted mt-0.5 truncate">{sub}</div>}
                      </div>
                      <span
                        className={`shrink-0 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                          kycOk
                            ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                            : "bg-amber-50 border-amber-300 text-amber-800"
                        }`}
                      >
                        KYC {c.kyc_status || "Not Submitted"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="px-5 py-4 border-t border-navy/10 flex items-center justify-between gap-3">
              <div className="text-[11px] text-ink-muted">
                {picked ? <>Selected: <span className="text-ink font-heading">{pickedLabel}</span></> : "Pick one to continue."}
              </div>
              <button
                onClick={() => setConfirming(true)}
                disabled={!picked}
                className="px-4 py-2 rounded bg-navy text-white text-xs font-heading hover:bg-[#256E58] disabled:opacity-40"
                data-testid="add-to-dr-next"
              >
                Next: Confirm →
              </button>
            </div>
          </>
        ) : (
          <div className="p-6 flex-1 min-h-0 overflow-y-auto space-y-4" data-testid="add-to-dr-confirm">
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
              <div>
                You&apos;re about to open a deal room between:
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-ink">
                  <div className="rounded border border-navy/10 bg-white px-3 py-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Startup</div>
                    <div className="font-heading">{isStartupMode ? pinnedLabel : pickedLabel}</div>
                  </div>
                  <div className="rounded border border-navy/10 bg-white px-3 py-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Investor</div>
                    <div className="font-heading">{isStartupMode ? pickedLabel : pinnedLabel}</div>
                  </div>
                </div>
                <div className="text-[11px] text-amber-800 mt-2">
                  A synthetic Interest record is created so downstream reporting stays consistent. If a room already exists between this pair we&apos;ll reuse it.
                </div>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">Deal summary (optional)</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                placeholder="e.g. Warm intro made at Fintech Roundtable — investor interested in Pre-A round of ₹8 Cr."
                className="w-full text-sm bg-white border border-navy/15 rounded p-2 text-ink focus:border-gold outline-none resize-none"
                data-testid="add-to-dr-summary"
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="text-xs font-heading text-ink-muted hover:text-ink"
                data-testid="add-to-dr-back"
              >
                ← Back to pick
              </button>
              <button
                onClick={submit}
                disabled={busy}
                className="px-5 py-2 rounded bg-gold text-navy text-xs font-heading font-semibold hover:bg-gold/90 disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid="add-to-dr-confirm"
              >
                {busy ? <><Loader2 size={13} className="animate-spin" /> Opening…</> : <><DoorOpen size={13} /> Confirm & open</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
